import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Card,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
  Divider,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { catalogAnalyticsService } from "../../utils/services/catalogAnalyticsService";
import { catalogService } from "../../utils/services/catalogService";
import { catalogAttributesService } from "../../utils/services/catalogAttributesService";
import DateRangePicker from "../date-range-picker";
import { isMobile } from "../../globalStyles/mobile";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import wsManager from "../../utils/websocketManager";
import { useDarkMode } from "../context/darkModeContext";

// Register plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n >= 1000
    ? `$${(n / 1000).toFixed(1)}k`
    : `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const fmtFull = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toAttributeOption = (value, labelPrefix = "") => ({
  value,
  label: labelPrefix ? `${labelPrefix}${value}` : String(value),
});

/** colour tier based on revenue vs max */
const barColor = (revenue, max) => {
  if (!max) return "#94a3b8";
  const ratio = revenue / max;
  if (ratio >= 0.6) return "#22c55e"; // green – high
  if (ratio >= 0.3) return "#f59e0b"; // amber – med
  return "#ef4444"; // red   – low
};

/**
 * Resolve a CSS custom property to its computed value.
 * Required for Chart.js because canvas fillStyle doesn't support CSS variables.
 */
const resolveVar = (varName, fallback = "") =>
  typeof document !== "undefined"
    ? getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim() || fallback
    : fallback;

// ─── KPI card ────────────────────────────────────────────────────────────────

function KpiCard({ title, value, subtitle, accent, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: "1 1 160px",
        minWidth: 140,
        p: 1.5,
        borderRadius: 2,
        border: selected
          ? "2px solid #3b82f6"
          : "1px solid var(--border-color)",
        backgroundColor: selected
          ? "rgba(59, 130, 246, 0.1)"
          : "var(--bg-paper)",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s",
        "&:hover": onClick ? { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" } : {},
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: "var(--text-tertiary)",
          fontFamily: "Poppins",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          fontFamily: "Poppins",
          color: "var(--text-primary)",
          lineHeight: 1.2,
          mt: 0.3,
        }}
      >
        {value}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            fontSize: 11,
            color: accent || "var(--text-tertiary)",
            fontFamily: "Poppins",
            mt: 0.3,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function CatalogAnalyticsChart({
  backgroundColor,
  border,
  minHeight,
  clientId,
  // New props for parent state management
  dateRange: propDateRange,
  onDateRangeChange,
  selectedCatalog: propSelectedCatalog,
  onCatalogChange,
  selectedPage: propSelectedPage,
  onPageChange,
  selectedStyle: propSelectedStyle,
  onStyleChange,
  selectedSku: propSelectedSku,
  onSkuChange,
  loading: propLoading,
  onLoadingChange,
  revenueChart: propRevenueChart,
  onRevenueChartChange,
  catalogMetrics: propCatalogMetrics,
  onCatalogMetricsChange,
  topPageAOV: propTopPageAOV,
  onTopPageAOVChange,
  catalogOptions: propCatalogOptions,
  onCatalogOptionsChange,
}) {
  const today = new Date();
  const { darkMode, colors } = useDarkMode();

  // ── chart labels plugin with dark mode support ──────────────────
  const chartLabelsPlugin = useMemo(
    () => ({
      id: "chartLabels",
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        const { data, chartArea } = chart;

        data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);

          meta.data.forEach((bar, index) => {
            const value = dataset.data[index];
            if (value === undefined || value === null) return;

            // Format the revenue value
            const label = fmt(value);

            // Position at constant right edge of chart area
            const x = chartArea.right - 8;
            const y = bar.y;

            // Chip styling
            ctx.font = `600 12px Poppins`;
            const metrics = ctx.measureText(label);
            const textWidth = metrics.width;
            const padding = 6;
            const chipHeight = 20;
            const chipWidth = textWidth + padding * 2;
            const chipX = x - chipWidth;
            const chipY = y - chipHeight / 2;

            // Draw rounded rectangle background
            const radius = 6;
            ctx.fillStyle = darkMode
              ? "rgba(42, 50, 75, 0.8)"
              : "rgba(240, 240, 240, 0.9)";
            ctx.beginPath();
            ctx.moveTo(chipX + radius, chipY);
            ctx.lineTo(chipX + chipWidth - radius, chipY);
            ctx.quadraticCurveTo(
              chipX + chipWidth,
              chipY,
              chipX + chipWidth,
              chipY + radius,
            );
            ctx.lineTo(chipX + chipWidth, chipY + chipHeight - radius);
            ctx.quadraticCurveTo(
              chipX + chipWidth,
              chipY + chipHeight,
              chipX + chipWidth - radius,
              chipY + chipHeight,
            );
            ctx.lineTo(chipX + radius, chipY + chipHeight);
            ctx.quadraticCurveTo(
              chipX,
              chipY + chipHeight,
              chipX,
              chipY + chipHeight - radius,
            );
            ctx.lineTo(chipX, chipY + radius);
            ctx.quadraticCurveTo(chipX, chipY, chipX + radius, chipY);
            ctx.closePath();
            ctx.fill();

            // Draw border
            ctx.strokeStyle = darkMode
              ? "rgba(100, 116, 139, 0.5)"
              : "rgba(200, 213, 219, 0.6)";
            ctx.lineWidth = 1;
            ctx.stroke();

            // Draw text
            ctx.fillStyle = colors.textPrimary;
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(label, chipX + padding, y);
          });
        });
      },
    }),
    [colors.textPrimary, darkMode],
  );

  // Use prop state if provided, otherwise use internal state
  const [internalDateRange, setInternalDateRange] = useState({
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: today,
    key: "selection",
  });
  const dateRange = propDateRange || internalDateRange;
  const setDateRange = onDateRangeChange || setInternalDateRange;

  const [internalSelectedCatalog, setInternalSelectedCatalog] = useState("all");
  const selectedCatalog = propSelectedCatalog ?? internalSelectedCatalog;
  const setSelectedCatalog = (val) => {
    onCatalogChange?.(val);
    setInternalSelectedCatalog(val);
  };

  const [internalSelectedPage, setInternalSelectedPage] = useState(null);
  const selectedPage = propSelectedPage ?? internalSelectedPage;
  const setSelectedPage = (val) => {
    onPageChange?.(val);
    setInternalSelectedPage(val);
  };

  const [internalSelectedStyle, setInternalSelectedStyle] = useState(null);
  const selectedStyle = propSelectedStyle ?? internalSelectedStyle;
  const setSelectedStyle = (val) => {
    onStyleChange?.(val);
    setInternalSelectedStyle(val);
  };

  const [internalSelectedSku, setInternalSelectedSku] = useState(null);
  const selectedSku = propSelectedSku ?? internalSelectedSku;
  const setSelectedSku = (val) => {
    onSkuChange?.(val);
    setInternalSelectedSku(val);
  };

  const [internalLoading, setInternalLoading] = useState(true);
  const loading = propLoading ?? internalLoading;
  const setLoading = (val) => {
    onLoadingChange?.(val);
    setInternalLoading(val);
  };

  const [internalRevenueChart, setInternalRevenueChart] = useState([]);
  const revenueChart = propRevenueChart ?? internalRevenueChart;
  const setRevenueChart = (val) => {
    onRevenueChartChange?.(val);
    setInternalRevenueChart(val);
  };

  const [internalCatalogMetrics, setInternalCatalogMetrics] = useState([]);
  const catalogMetrics = propCatalogMetrics ?? internalCatalogMetrics;
  const setCatalogMetrics = (val) => {
    onCatalogMetricsChange?.(val);
    setInternalCatalogMetrics(val);
  };

  const [internalTopPageAOV, setInternalTopPageAOV] = useState([]);
  const topPageAOV = propTopPageAOV ?? internalTopPageAOV;
  const setTopPageAOV = (val) => {
    onTopPageAOVChange?.(val);
    setInternalTopPageAOV(val);
  };

  const [internalCatalogOptions, setInternalCatalogOptions] = useState([]);
  const catalogOptions = propCatalogOptions ?? internalCatalogOptions;
  const setCatalogOptions = (val) => {
    onCatalogOptionsChange?.(val);
    setInternalCatalogOptions(val);
  };

  // ── load catalog list for dropdown ─────────────────────────────
  useEffect(() => {
    catalogService
      .fetchNames("", clientId, 100, 0)
      .then((res) => {
        const list = res?.data || res || [];
        setCatalogOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => setCatalogOptions([]));
  }, [clientId]);

  const selectedCatalogUid =
    selectedCatalog && selectedCatalog !== "all" ? selectedCatalog : undefined;

  const fetchAttributeOptions = useCallback(
    async (key, search, page, limit, labelPrefix = "") => {
      try {
        const res = await catalogAttributesService.fetch({
          key,
          search: search || undefined,
          client_id: clientId || undefined,
          catalog_uid: selectedCatalogUid,
          page,
          limit,
        });

        return {
          data: (res?.data || []).map((value) =>
            toAttributeOption(value, labelPrefix),
          ),
        };
      } catch (error) {
        console.error(`Error fetching ${key}:`, error);
        return { data: [] };
      }
    },
    [clientId, selectedCatalogUid],
  );

  const fetchPageOptions = useCallback(
    (limit, skip, search) =>
      fetchAttributeOptions(
        "products.page",
        search,
        Math.floor(skip / limit) + 1,
        limit,
        "Page ",
      ),
    [fetchAttributeOptions],
  );

  const fetchStyleOptions = useCallback(
    (limit, skip, search) =>
      fetchAttributeOptions(
        "variants.option.value",
        search,
        Math.floor(skip / limit) + 1,
        limit,
      ),
    [fetchAttributeOptions],
  );

  const fetchSKUOptions = useCallback(
    (limit, skip, search) =>
      fetchAttributeOptions(
        "variants.sku",
        search,
        Math.floor(skip / limit) + 1,
        limit,
      ),
    [fetchAttributeOptions],
  );

  // ── fetch analytics ─────────────────────────────────────────────
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      // Normalize dates: remove time portion to ensure consistent cache keys
      const normalizeDate = (date) => {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0); // Set to midnight
        return normalized.toISOString().split("T")[0] + "T00:00:00Z"; // YYYY-MM-DDTHH:mm:ssZ
      };

      const res = await catalogAnalyticsService.fetch({
        client_id: clientId || null,
        start_date: normalizeDate(dateRange.startDate),
        end_date: normalizeDate(dateRange.endDate),
        catalog_uid: selectedCatalog !== "all" ? selectedCatalog : null,
        page: selectedPage ? Number(selectedPage.value) : null,
        selectedSku: selectedSku ? selectedSku.value : null,
        selected_style: selectedStyle ? selectedStyle.value : null,
      });
      setRevenueChart(res?.revenueChart || []);
      setCatalogMetrics(res?.catalogMetrics || []);
      setTopPageAOV(
        Array.isArray(res?.topPageAOVKpi)
          ? res.topPageAOVKpi
          : [res?.topPageAOVKpi || {}],
      );
    } catch {
      setRevenueChart([]);
      setCatalogMetrics([]);
      setTopPageAOV([]);
    } finally {
      setLoading(false);
    }
  }, [
    clientId,
    dateRange,
    selectedCatalog,
    selectedPage,
    selectedStyle,
    selectedSku,
  ]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // ── listen for WebSocket background refresh ─────────────────────
  const filtersRef = useRef({
    clientId,
    dateRange,
    selectedCatalog,
    selectedPage,
    selectedStyle,
  });
  useEffect(() => {
    filtersRef.current = {
      clientId,
      dateRange,
      selectedCatalog,
      selectedPage,
      selectedStyle,
    };
  }, [clientId, dateRange, selectedCatalog, selectedPage, selectedStyle]);

  useEffect(() => {
    const unsubscribe = wsManager.on("catalogAnalyticsRefresh", (message) => {
      const freshData = message?.data;
      if (!freshData) return;
      setRevenueChart(freshData.revenueChart || []);
      setCatalogMetrics(freshData.catalogMetrics || []);
      setTopPageAOV(
        Array.isArray(freshData.topPageAOVKpi)
          ? freshData.topPageAOVKpi
          : [freshData?.topPageAOVKpi || {}],
      );
    });
    return () => unsubscribe();
  }, []);

  // ── Determine view mode and filter data ──────────────────────────
  const isAllCatalogs = selectedCatalog === "all" || !selectedCatalog;

  // Backend groups by page when catalog_uid is provided; use revenueChart directly
  const displayChartData = revenueChart;

  // ── derived KPI values ──────────────────────────────────────────
  // Filter metrics based on selected catalog
  const relevantMetrics = isAllCatalogs
    ? catalogMetrics
    : catalogMetrics.filter((c) => c.catalogId === selectedCatalog);

  const totalRevenue = relevantMetrics.reduce(
    (s, c) => s + (c.totalRevenue || 0),
    0,
  );
  const totalOrders = relevantMetrics.reduce(
    (s, c) => s + (c.totalOrders || 0),
    0,
  );
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const editionCount = relevantMetrics.length;
  const maxRevenue =
    displayChartData.length > 0 ? displayChartData[0].revenue : 0;
  const minRevenue =
    displayChartData.length > 0
      ? displayChartData[displayChartData.length - 1].revenue
      : 0;
  const revenueGapPct =
    maxRevenue > 0
      ? Math.round(((maxRevenue - minRevenue) / maxRevenue) * 100)
      : 0;
  const totalStyles = relevantMetrics.reduce(
    (s, c) => s + (c.stylesInCatalog || 0),
    0,
  );

  // Top 3 catalog/page share
  const top3Revenue = displayChartData
    .slice(0, 3)
    .reduce((s, c) => s + c.revenue, 0);
  const top3Pct =
    totalRevenue > 0 ? Math.round((top3Revenue / totalRevenue) * 100) : 0;

  // Top page AOV by revenue
  const topPageMetric = isAllCatalogs
    ? topPageAOV.reduce(
        (max, current) =>
          (current.topPageAOV || 0) > (max?.topPageAOV || 0) ? current : max,
        topPageAOV[0] || {},
      )
    : topPageAOV[0] || {};

  const topPageAOVValue = topPageMetric.topPageAOV || 0;
  const topPageDescription = topPageMetric.description || "N/A";

  // ── chart config ────────────────────────────────────────────────
  const chartData = {
    labels: isAllCatalogs
      ? displayChartData.map((c) => c.catalogId || c.catalogName)
      : displayChartData.map((c) => c.catalogName), // For single catalog, label is the name
    datasets: [
      {
        label: isAllCatalogs ? "Revenue ($)" : "Revenue by Page ($)",
        data: displayChartData.map((c) => c.revenue),
        backgroundColor: displayChartData.map((c) =>
          barColor(
            c.revenue,
            Math.max(...displayChartData.map((x) => x.revenue), 1),
          ),
        ),
        borderRadius: 4,
        barThickness: isMobile() ? 16 : 20,
        minBarLength: displayChartData.length > 10 ? 12 : 8,
      },
    ],
  };

  // ── compute explicit x max so both charts share the same scale ──
  const xAxisMax =
    displayChartData.length > 0
      ? Math.max(...displayChartData.map((c) => c.revenue)) * 1.15
      : undefined;

  const xScaleBase = {
    beginAtZero: true,
    max: xAxisMax,
    grid: {
      color: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
      lineWidth: 1,
    },
    ticks: {
      callback: (v) => fmt(v),
      font: { size: isMobile() ? 9 : 11 },
      color: resolveVar("--text-secondary", "#64748b"),
    },
  };

  const yScaleBase = {
    grid: { display: false },
    ticks: {
      font: { size: isMobile() ? 10 : 12 },
      color: resolveVar("--text-secondary", "#64748b"),
    },
  };

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      chartLabels: {},
      legend: { display: false },
      tooltip: {
        backgroundColor: resolveVar("--bg-paper", "#ffffff"),
        titleColor: resolveVar("--text-primary", "#1e293b"),
        bodyColor: resolveVar("--text-primary", "#1e293b"),
        borderColor: resolveVar("--border-color", "#e2e8f0"),
        borderWidth: 1,
        callbacks: {
          label: (ctx) => ` ${fmtFull(ctx.raw)}`,
        },
      },
    },
    scales: {
      x: { ...xScaleBase },
      y: yScaleBase,
    },
  };

  // ── fixed x-axis chart options (bars invisible, y-labels transparent for alignment) ──
  const axisChartData = {
    labels: chartData.labels,
    datasets: [
      {
        ...chartData.datasets[0],
        backgroundColor: "transparent",
        borderColor: "transparent",
      },
    ],
  };

  const axisChartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: xScaleBase,
      y: {
        ...yScaleBase,
        ticks: {
          ...yScaleBase.ticks,
          color: "transparent", // invisible but reserves same left margin
        },
      },
    },
  };

  // ── dropdown sx helper ──────────────────────────────────────────
  const dropdownSx = {
    height: 34,
    fontSize: 13,
    fontFamily: "Poppins",
    backgroundColor: "var(--bg-paper)",
    color: "var(--text-primary)",
    borderRadius: 2,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--border-color)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "var(--text-secondary)",
    },
    "& .MuiSvgIcon-root": { color: "var(--text-secondary)" },
    minWidth: 130,
  };

  return (
    <Card
      sx={{
        backgroundColor: backgroundColor || "var(--bg-paper)",
        border: border || "1px solid var(--border-color)",
        borderRadius: 2,
        p: isMobile() ? 1.5 : 2,
        margin: 1,
      }}
    >
      {/* ── header row ── */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            fontFamily: "Poppins",
            color: "var(--text-primary)",
            mr: 1,
          }}
        >
          Catalog analytics
        </Typography>

        {/* Catalog dropdown */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            sx={{
              fontSize: 12,
              color: "var(--text-tertiary)",
              fontFamily: "Poppins",
            }}
          >
            Catalog
          </Typography>
          <FormControl size="small">
            <Select
              value={selectedCatalog}
              onChange={(e) => {
                console.log(e?.target);

                setSelectedCatalog(e.target.value);
                // Clear page and style filters when catalog changes
                setSelectedPage(null);
                setSelectedStyle(null);
                setSelectedSku(null);
              }}
              sx={dropdownSx}
              displayEmpty
            >
              <MenuItem value="all">All editions</MenuItem>
              {catalogOptions.map((c) => (
                <MenuItem key={c.name} value={c.uid}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Page autocomplete */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 130,
            opacity: isAllCatalogs ? 1 : 0.5,
            pointerEvents: isAllCatalogs ? "auto" : "none",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "var(--text-tertiary)",
              fontFamily: "Poppins",
            }}
          >
            Page
          </Typography>
          <AutocompleteWithPagination
            selectedItem={selectedPage}
            onDataChange={(value) =>
              setSelectedPage(value && typeof value === "object" ? value : null)
            }
            width={140}
            optionKey="label"
            hideLabel
            placeholder="All pages"
            backgroundColor="var(--bg-paper)"
            apiEndPoint={fetchPageOptions}
            disabled={!isAllCatalogs}
            inputSx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 13,
                fontFamily: "Poppins",
                backgroundColor: "var(--bg-paper)",
                color: "var(--text-primary)",
                "& fieldset": { borderColor: "var(--border-color)" },
                "&:hover fieldset": {
                  borderColor: "var(--text-secondary)",
                },
                height: 34,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 130,
            opacity: isAllCatalogs ? 1 : 0.5,
            pointerEvents: isAllCatalogs ? "auto" : "none",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "var(--text-tertiary)",
              fontFamily: "Poppins",
            }}
          >
            SKU
          </Typography>
          <AutocompleteWithPagination
            selectedItem={selectedSku}
            onDataChange={(value) =>
              setSelectedSku(value && typeof value === "object" ? value : null)
            }
            width={140}
            optionKey="label"
            hideLabel
            placeholder="All SKUs"
            backgroundColor="var(--bg-paper)"
            apiEndPoint={fetchSKUOptions}
            disabled={!isAllCatalogs}
            inputSx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 13,
                fontFamily: "Poppins",
                backgroundColor: "var(--bg-paper)",
                color: "var(--text-primary)",
                "& fieldset": { borderColor: "var(--border-color)" },
                "&:hover fieldset": {
                  borderColor: "var(--text-secondary)",
                },
                height: 34,
              },
            }}
          />
        </Box>

        {/* Style autocomplete */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 130,
            opacity: isAllCatalogs ? 1 : 0.5,
            pointerEvents: isAllCatalogs ? "auto" : "none",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "var(--text-tertiary)",
              fontFamily: "Poppins",
            }}
          >
            Style
          </Typography>
          <AutocompleteWithPagination
            selectedItem={selectedStyle}
            onDataChange={(value) =>
              setSelectedStyle(
                value && typeof value === "object" ? value : null,
              )
            }
            width={140}
            optionKey="label"
            hideLabel
            placeholder="All styles"
            backgroundColor="var(--bg-paper)"
            apiEndPoint={fetchStyleOptions}
            disabled={!isAllCatalogs}
            inputSx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 13,
                fontFamily: "Poppins",
                backgroundColor: "var(--bg-paper)",
                color: "var(--text-primary)",
                "& fieldset": { borderColor: "var(--border-color)" },
                "&:hover fieldset": {
                  borderColor: "var(--text-secondary)",
                },
                height: 34,
              },
            }}
          />
        </Box>

        {/* Date range – pushes to right on wider screens */}
        <Box sx={{ ml: "auto" }}>
          <DateRangePicker
            selectionRange={dateRange}
            setSelectionRange={setDateRange}
          />
        </Box>
      </Box>

      {/* ── KPI cards ── */}
      {loading ? (
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rounded" width={160} height={72} />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
          <KpiCard
            title="Total revenue"
            value={fmtFull(totalRevenue).replace(".00", "")}
            subtitle={
              isAllCatalogs
                ? `${top3Pct}% from top 3 catalogs`
                : `${totalOrders} orders`
            }
            accent="#22c55e"
            selected
          />
          <KpiCard
            title="Avg order value"
            value={`$${Math.round(avgOrderValue)}`}
            subtitle={
              revenueChart.length >= 2 && isAllCatalogs
                ? `Range $${Math.round(revenueChart[revenueChart.length - 1]?.avgOrderValue || 0)} – $${Math.round(revenueChart[0]?.avgOrderValue || 0)}`
                : isAllCatalogs
                  ? undefined
                  : `${displayChartData.length || 0} page${displayChartData.length !== 1 ? "s" : ""}`
            }
          />
          <KpiCard
            title={isAllCatalogs ? "Catalog comparison" : "Performance"}
            value={
              isAllCatalogs
                ? `${editionCount} editions`
                : `${displayChartData.length} pages`
            }
            subtitle={
              isAllCatalogs
                ? revenueGapPct > 0
                  ? `▼ ${revenueGapPct}% gap top vs bottom`
                  : "No gap data"
                : revenueChart.length >= 2
                  ? `Range $${Math.round(revenueChart[revenueChart.length - 1]?.avgOrderValue || 0)} – $${Math.round(revenueChart[0]?.avgOrderValue || 0)} AOV`
                  : undefined
            }
            accent={
              isAllCatalogs
                ? revenueGapPct > 50
                  ? "#ef4444"
                  : "#f59e0b"
                : "#3b82f6"
            }
          />
          <KpiCard
            title="Style yield"
            value={`${totalStyles} styles`}
            subtitle={
              isAllCatalogs
                ? catalogMetrics.length > 0
                  ? `Top catalog: ${catalogMetrics[0]?.stylesInCatalog || 0}`
                  : undefined
                : relevantMetrics.length > 0
                  ? `Catalog total: ${relevantMetrics[0]?.stylesInCatalog || 0}`
                  : undefined
            }
          />
          <KpiCard
            title="Top page AOV"
            value={`$${Math.round(topPageAOVValue)}`}
            subtitle={
              isAllCatalogs
                ? topPageDescription
                : displayChartData.length > 0
                  ? `Best: $${Math.round(displayChartData[0]?.avgOrderValue || 0)}`
                  : undefined
            }
            accent="#3b82f6"
          />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Skeleton variant="rounded" width="100%" height={minHeight} />
      ) : displayChartData.length === 0 ? (
        <Box
          sx={{
            height: minHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              color: "var(--text-tertiary)",
              fontFamily: "Poppins",
              fontSize: 13,
            }}
          >
            {isAllCatalogs
              ? "No revenue data for selected filters"
              : `No page data for ${
                  catalogOptions.find((c) => c.uid === selectedCatalog)?.uid ||
                  selectedCatalog
                }`}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              width: "100%",
              height: minHeight,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: Math.max(minHeight, displayChartData.length * 50 + 30),
              }}
            >
              <Bar
                key={`chart-${colors.textPrimary}`}
                data={chartData}
                options={chartOptions}
                plugins={[chartLabelsPlugin]}
              />
            </Box>
          </Box>
          {/* Fixed x|| 400-axis
          <Box sx={{ width: "100%", height: 48 }}>
            <Bar
              key={`axis-${colors.textPrimary}`}
              data={axisChartData}
              options={axisChartOptions}
            />
          </Box> */}
        </Box>
      )}

      {/* ── tier legend + footer ── */}
      {!loading && displayChartData.length > 0 && (
        <>
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              borderRadius: 1.5,
              backgroundColor: "var(--bg-secondary)",
              border: "1px solid var(--border-color)",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: "var(--text-secondary)",
                fontFamily: "Poppins",
              }}
            >
              {isAllCatalogs ? (
                <>
                  Total&nbsp;
                  <strong>
                    {editionCount} edition{editionCount !== 1 ? "s" : ""}
                  </strong>
                  &nbsp;·&nbsp;
                  <strong>{fmtFull(totalRevenue)}</strong> revenue&nbsp;·&nbsp;
                  <strong>{totalOrders}</strong> orders
                </>
              ) : (
                <>
                  Catalog&nbsp;
                  <strong>
                    {catalogOptions.find((c) => c.uid === selectedCatalog)
                      ?.uid || selectedCatalog}
                  </strong>
                  &nbsp;·&nbsp;
                  <strong>{displayChartData.length} pages</strong>&nbsp;·&nbsp;
                  <strong>{fmtFull(totalRevenue)}</strong> revenue&nbsp;·&nbsp;
                  <strong>{totalOrders}</strong> orders
                </>
              )}
            </Typography>
          </Box>
        </>
      )}
    </Card>
  );
}
